import { SisuContext } from '@wix/da-shared-react/pkg/Context/SisuContext';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import { ThemeOptions } from '@wix/da-react-context/pkg/ThemeContext';
import LoginPage from '../Pages/LoginPage';
import LogoutOauthPage from '../Pages/LogoutOauthPage';
import ForgotPage from '../Pages/ForgotPage';
import IntentsPage from '../Pages/IntentsPage';
import ContentFilterPage from '../Pages/ContentFilterPage';
import TopicsPage from '../Pages/TopicsPage';
import JoinOrIntentsPage from '../Pages/JoinOrIntentsPage';
import TFAVerificationPage from '../Pages/TFAVerificationPage';
import AppAuthorizationPage from '../Pages/AppAuthorizationPage';
import ResetPasswordPage from '../Pages/ResetPasswordPage';
import MuroModalClosePage from '../Pages/MuroModalClosePage';
import ManageCookiesPage from '../Pages/ManageCookiesPage';
import DiscordConnectedPage from '../Pages/DiscordConnectedPage';
import PasswordPage from '../Pages/PasswordPage';
import TFAReminderPage from '../Pages/TFAReminderPage';
import decorateComponentWithProps from 'decorate-component-with-props';
export interface Props {
  loggedin?: boolean;
  language: string;
  referer?: string;
  baseDaUrl?: string;
  environment?: RuntimeEnvironment;
  isTFASignIn?: boolean;
  page: string;
  forceRenderPage?: string;
}

// Helper functions for authentication checks
const shouldShowLoggedoutRoute = (loggedin: boolean): boolean => !loggedin;
const shouldShowLoggedinRoute = (loggedin: boolean): boolean => loggedin;

const App: React.FC<Props> = props => {
  const {
    language,
    environment,
    page,
    loggedin = false,
    isTFASignIn,
    forceRenderPage,
  } = props;

  const renderPage = () => {
    // Use forceRenderPage if it's set, otherwise use the regular page
    const currentPage = forceRenderPage || page;

    // Define page mappings
    const pageComponents: { [key: string]: React.ComponentType<any> } = {
      // signin flow
      forgot: ForgotPage,
      step2: PasswordPage,
      login: LoginPage,
      'logout-oauth2': LogoutOauthPage,
      'tfa-reminder': TFAReminderPage,

      // signup flow
      intent: IntentsPage,
      'content-filter': ContentFilterPage,
      topics: TopicsPage,

      // Reset password page
      'reset-password': ResetPasswordPage,

      // 3rd party apps oauth flow
      applications: AppAuthorizationPage,

      // OAuth route - handled server-side
      oauth2: () => <></>,
      join: JoinOrIntentsPage,

      // modal routes
      closemodal: MuroModalClosePage,

      // post routes
      signin: isTFASignIn
        ? TFAVerificationPage
        : decorateComponentWithProps(LoginPage, { isSignin: true }),
      'signin-tfa': TFAVerificationPage,
      'forgot-post': ForgotPage,
      signup: JoinOrIntentsPage,
      signup2: JoinOrIntentsPage,
      'reset-password-post': ResetPasswordPage,

      // Root
      'discord-done': DiscordConnectedPage,
      cookies: ManageCookiesPage,
    };

    // Get the component for the current page
    const Component = pageComponents[currentPage];

    if (!Component) {
      // Default fallback
      if (shouldShowLoggedoutRoute(loggedin)) {
        return <></>;
      }
      return null;
    }

    // Check authentication requirements for specific pages
    const requiresLoggedOut = [
      'forgot',
      'step2',
      'login',
      'reset-password',
      'oauth2',
      'join',
      'signin',
      'signin-tfa',
      'forgot-post',
      'reset-password-post',
    ];
    const requiresLoggedIn = [
      'logout-oauth2',
      'tfa-reminder',
      'intent',
      'content-filter',
      'topics',
      'applications',
    ];

    if (
      requiresLoggedOut.includes(currentPage) &&
      !shouldShowLoggedoutRoute(loggedin)
    ) {
      return null;
    }

    if (
      requiresLoggedIn.includes(currentPage) &&
      !shouldShowLoggedinRoute(loggedin)
    ) {
      return null;
    }

    // Special condition for TFA signin
    if (currentPage === 'signin-tfa' && !isTFASignIn) {
      return null;
    }

    return <Component />;
  };

  return (
    <SisuContext.Provider value={true}>
      <DaApp
        environment={environment}
        language={language}
        theme={ThemeOptions.LIGHT}
        disableHeadTags={true}
      >
        {renderPage()}
      </DaApp>
    </SisuContext.Provider>
  );
};

export default App;
